/**
 * Laura Insights Dashboard
 *
 * Displays proactive insights generated by Laura AI,
 * including production efficiency, delivery routes,
 * inventory forecasts, and customer patterns.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './LauraInsights.css';

interface Insight {
  id: number;
  title: string;
  summary: string;
  full_analysis: string;
  recommendation: string;
  category: string;
  priority: string;
  status: string;
  action_link: string;
  potential_savings: number | null;
  potential_revenue: number | null;
  created_at: string;
  expires_at: string | null;
}

interface Summary {
  total: number;
  urgent: number;
  new: number;
  by_category: Record<string, number>;
  by_priority: Record<string, number>;
}

const categoryLabels: Record<string, string> = {
  production: 'Production',
  delivery: 'Delivery',
  inventory: 'Inventory',
  customer: 'Customer',
  financial: 'Financial',
  operational: 'Operational',
  alert: 'Alert',
};

const priorityColors: Record<string, string> = {
  info: '#6b7280',
  suggestion: '#3b82f6',
  attention: '#f59e0b',
  urgent: '#ef4444',
};

const LauraInsights: React.FC = () => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState<Insight[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null);
  const [runningAnalysis, setRunningAnalysis] = useState(false);

  useEffect(() => {
    fetchInsights();
    fetchSummary();
  }, [selectedCategory]);

  const fetchInsights = async () => {
    try {
      const params: Record<string, string> = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      const response = await api.get('/insights/', { params });
      setInsights(response.data);
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/insights/summary/');
      setSummary(response.data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  const runAnalysis = async () => {
    setRunningAnalysis(true);
    try {
      await api.post('/insights/run-analysis/');
      await fetchInsights();
      await fetchSummary();
    } catch (err) {
      console.error('Failed to run analysis:', err);
    } finally {
      setRunningAnalysis(false);
    }
  };

  const handleAction = async (insightId: number, action: 'acknowledge' | 'act' | 'dismiss') => {
    try {
      await api.post(`/insights/${insightId}/${action}/`);
      setInsights(insights.filter(i => i.id !== insightId));
      if (selectedInsight?.id === insightId) {
        setSelectedInsight(null);
      }
      fetchSummary();
    } catch (err) {
      console.error(`Failed to ${action} insight:`, err);
    }
  };

  const navigateToAction = (link: string) => {
    if (link) {
      if (link.startsWith('/')) {
        navigate(link);
      } else {
        window.location.href = link;
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="laura-insights-page">
      <div className="insights-header">
        <div className="header-title">
          <div className="laura-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z"/>
              <path d="M12 8v4l3 3"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </div>
          <div>
            <h1>Laura's Insights</h1>
            <p>Proactive analysis and recommendations for your operations</p>
          </div>
        </div>
        <button
          className="run-analysis-btn"
          onClick={runAnalysis}
          disabled={runningAnalysis}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={runningAnalysis ? 'spinning' : ''}
          >
            <path d="M23 4v6h-6"/>
            <path d="M1 20v-6h6"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
            <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
          </svg>
          {runningAnalysis ? 'Analyzing...' : 'Run Analysis Now'}
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="insights-summary">
          <div className="summary-card total">
            <span className="summary-number">{summary.total}</span>
            <span className="summary-label">Total Insights</span>
          </div>
          <div className="summary-card urgent">
            <span className="summary-number">{summary.urgent}</span>
            <span className="summary-label">Need Attention</span>
          </div>
          <div className="summary-card new">
            <span className="summary-number">{summary.new}</span>
            <span className="summary-label">New</span>
          </div>
          {Object.entries(summary.by_category).map(([cat, count]) => (
            <div
              key={cat}
              className={`summary-card category ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat === selectedCategory ? 'all' : cat)}
            >
              <span className="summary-number">{count}</span>
              <span className="summary-label">{categoryLabels[cat] || cat}</span>
            </div>
          ))}
        </div>
      )}

      {/* Category Filter */}
      <div className="insights-filter">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
        </svg>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="all">All Categories</option>
          <option value="production">Production</option>
          <option value="delivery">Delivery & Routes</option>
          <option value="inventory">Inventory</option>
          <option value="customer">Customers</option>
          <option value="financial">Financial</option>
        </select>
      </div>

      {/* Insights Container */}
      <div className="insights-container">
        <div className="insights-list">
          {loading ? (
            <div className="loading">Loading insights...</div>
          ) : insights.length === 0 ? (
            <div className="no-insights">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z"/>
                <path d="M12 8v4l3 3"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <h3>No insights right now</h3>
              <p>Laura is keeping an eye on things. Check back later or run analysis manually.</p>
            </div>
          ) : (
            insights.map(insight => (
              <div
                key={insight.id}
                className={`insight-card priority-${insight.priority} ${selectedInsight?.id === insight.id ? 'selected' : ''}`}
                onClick={() => setSelectedInsight(insight)}
              >
                <div className="insight-header">
                  <span className="insight-category">
                    {categoryLabels[insight.category] || insight.category}
                  </span>
                  <span
                    className="insight-priority"
                    style={{ backgroundColor: priorityColors[insight.priority] }}
                  >
                    {insight.priority}
                  </span>
                </div>
                <h3 className="insight-title">{insight.title}</h3>
                <p className="insight-summary">{insight.summary}</p>
                {(insight.potential_savings || insight.potential_revenue) && (
                  <div className="insight-value">
                    {insight.potential_savings && (
                      <span className="savings">
                        Potential savings: ${insight.potential_savings.toLocaleString()}
                      </span>
                    )}
                    {insight.potential_revenue && (
                      <span className="revenue">
                        Potential revenue: ${insight.potential_revenue.toLocaleString()}
                      </span>
                    )}
                  </div>
                )}
                <div className="insight-actions">
                  {insight.action_link && (
                    <button
                      className="action-btn primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToAction(insight.action_link);
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                      Take Action
                    </button>
                  )}
                  <button
                    className="action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAction(insight.id, 'acknowledge');
                    }}
                    title="Acknowledge"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  </button>
                  <button
                    className="action-btn dismiss"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAction(insight.id, 'dismiss');
                    }}
                    title="Dismiss"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Detail Panel */}
        {selectedInsight && (
          <div className="insight-detail">
            <div className="detail-header">
              <h2>{selectedInsight.title}</h2>
              <button onClick={() => setSelectedInsight(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="detail-meta">
              <span className="meta-category">
                {categoryLabels[selectedInsight.category] || selectedInsight.category}
              </span>
              <span
                className="meta-priority"
                style={{ backgroundColor: priorityColors[selectedInsight.priority] }}
              >
                {selectedInsight.priority}
              </span>
              <span className="meta-date">
                {formatDate(selectedInsight.created_at)}
              </span>
            </div>

            <div className="detail-section">
              <h4>Analysis</h4>
              <pre className="analysis-text">{selectedInsight.full_analysis}</pre>
            </div>

            {selectedInsight.recommendation && (
              <div className="detail-section recommendation">
                <h4>Laura's Recommendation</h4>
                <p>{selectedInsight.recommendation}</p>
              </div>
            )}

            <div className="detail-actions">
              {selectedInsight.action_link && (
                <button
                  className="detail-action-btn primary"
                  onClick={() => navigateToAction(selectedInsight.action_link)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                  Take Action
                </button>
              )}
              <button
                className="detail-action-btn"
                onClick={() => handleAction(selectedInsight.id, 'act')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                Mark as Acted On
              </button>
              <button
                className="detail-action-btn dismiss"
                onClick={() => handleAction(selectedInsight.id, 'dismiss')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LauraInsights;
